import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, MapPin, Ticket, Trophy } from "lucide-react";
import { fetchList } from "../api";
import { SectionHeading } from "../components/Shared";
import { NewsMedia } from "../components/NewsMedia";

const fmtDate = (d) => {
  if (!d) return "Date TBA";
  const dt = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T00:00:00") : new Date(d);
  if (isNaN(dt)) return "Date TBA";
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

export default function Home() {
  const [wrestlers, setWrestlers] = useState([]);
  const [events, setEvents] = useState([]);
  const [champs, setChamps] = useState([]);
  const [news, setNews] = useState([]);

  useEffect(() => {
    fetchList("wrestlers").then(setWrestlers).catch(() => {});
    fetchList("events").then(setEvents).catch(() => {});
    fetchList("championships").then(setChamps).catch(() => {});
    fetchList("news").then(setNews).catch(() => {});
  }, []);

  const upcoming = events.filter((e) => e.status === "upcoming").slice(0, 3);
  const nextEvent = upcoming[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://customer-assets.emergentagent.com/job_wrestling-federation/artifacts/nkiznswr_Event%20Image.jpg"
            alt="CWF live event"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/70 to-[#050505]/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
        </div>

        <div className="relative z-10 px-6 md:px-12 lg:px-20 w-full spotlight">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-10 h-[2px] bg-brand" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Independent Pro Wrestling</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl uppercase leading-[0.9] tracking-tighter">
              Built for the fans.<br />
              <span className="text-brand">Powered by the community.</span>
            </h1>
            <p className="text-zinc-300 text-lg max-w-xl mt-6 leading-relaxed">
              Championship Wrestling Federation brings Tyler, Texas hard-hitting, family-friendly wrestling action — where every fan is part of the show.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/events" data-testid="hero-tickets-cta" className="bg-brand hover:bg-brand-hi text-black font-display text-2xl uppercase tracking-wide px-9 py-4 flex items-center gap-2 transition-colors">
                <Calendar size={22} /> Upcoming Events
              </Link>
              <Link to="/roster" data-testid="hero-roster-cta" className="border-2 border-zinc-700 hover:border-white text-white font-display text-2xl uppercase tracking-wide px-9 py-4 transition-colors">
                Meet The Roster
              </Link>
            </div>
          </motion.div>
        </div>

        {nextEvent && (
          <div className="absolute bottom-0 left-0 right-0 z-10 bg-brand text-black overflow-hidden py-3">
            <div className="flex whitespace-nowrap animate-marquee">
              {Array(6).fill(0).map((_, i) => (
                <span key={i} className="font-display text-xl uppercase tracking-wide px-6 flex items-center gap-3">
                  Next Event: {nextEvent.title} <span className="text-black/50">·</span> {fmtDate(nextEvent.date)} <span className="text-black/50">·</span> Free Admission <span className="text-black/50">·</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* FEDERATION NEWS */}
      {news.length > 0 && (
        <section className="px-6 md:px-12 lg:px-20 py-24 bg-[#0a0a0a]" data-testid="home-news">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <SectionHeading kicker="Latest" title="Federation News" className="mb-0" />
            <Link to="/news" className="font-display text-xl uppercase text-brand hover:text-brand-hi flex items-center gap-2">
              All News <ArrowRight size={18} />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {news.slice(0, 3).map((n) => (
              <Link key={n.id} to={`/news/${n.id}`} data-testid={`home-news-${n.id}`} className="group">
                <div className="aspect-video overflow-hidden border border-zinc-800 mb-4">
                  <NewsMedia src={n.home_image || n.image} poster={n.poster} alt={n.title} className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${(n.home_image || n.image).includes("tourney") || (n.home_image || n.image).includes("lt763n1a") || (n.home_image || n.image).includes("j2k9i302") || (n.home_image || n.image).includes("4r42yp65") || (n.home_image || n.image).includes("2twvtqsw") || (n.home_image || n.image).includes("cgktuqth") || (n.home_image || n.image).includes("742511545") ? "object-[center_20%]" : ""}`} />
                </div>
                <span className="font-mono text-xs uppercase tracking-widest text-brand">{n.category}</span>
                <h3 className="font-display text-2xl uppercase tracking-tight mt-2 group-hover:text-brand transition-colors leading-tight">{n.title}</h3>
                <p className="text-zinc-500 mt-2 line-clamp-2">{n.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* UPCOMING EVENTS */}
      <section className="px-6 md:px-12 lg:px-20 py-24 bg-[#050505]" data-testid="home-events">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading kicker="Don't Miss Out" title="Upcoming Shows" className="mb-0" />
          <Link to="/events" className="font-display text-xl uppercase text-brand hover:text-brand-hi flex items-center gap-2">
            All Events <ArrowRight size={18} />
          </Link>
        </div>
        <div className="space-y-4">
          {upcoming.map((ev, i) => (
            <motion.div
              key={ev.id}
              data-testid={`home-event-${ev.id}`}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex flex-col md:flex-row md:items-center gap-6 bg-[#111] border-l-4 border-brand p-6 hover:bg-[#161616] transition-colors"
            >
              <div className="md:w-40 shrink-0">
                <div className="font-display text-4xl text-brand leading-none">{fmtDate(ev.date)}</div>
              </div>
              <div className="flex-1">
                <h3 className="font-display text-3xl uppercase tracking-tight">{ev.title}</h3>
                <div className="flex flex-wrap gap-4 text-zinc-400 mt-1 text-sm">
                  {(ev.venue || ev.city) && <span className="flex items-center gap-1"><MapPin size={15} /> {[ev.venue, ev.city].filter(Boolean).join(", ")}</span>}
                  <span className="text-brand font-mono uppercase text-xs tracking-wider self-center">Free Admission</span>
                  {ev.main_event && <span className="text-zinc-400 font-mono uppercase text-xs tracking-wider self-center">Main: {ev.main_event}</span>}
                </div>
              </div>
              <Link to="/events" className="bg-white text-black font-display text-xl uppercase px-7 py-3 hover:bg-brand hover:text-black transition-colors text-center">
                Event Details
              </Link>
            </motion.div>
          ))}
          {upcoming.length === 0 && <p className="text-zinc-500">No upcoming events announced yet.</p>}
        </div>
      </section>

      {/* CHAMPIONS */}
      <section className="px-6 md:px-12 lg:px-20 py-24 bg-[#0a0a0a]" data-testid="home-champions">
        <SectionHeading kicker="Who Holds The Gold?" title="Reigning Champions" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {champs.map((c, i) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="relative group border border-zinc-800 overflow-hidden">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
              </div>
              <div className="absolute bottom-0 p-6">
                <div className="flex items-center gap-2 text-brand mb-1"><Trophy size={16} /><span className="font-mono text-xs uppercase tracking-widest">{c.division}</span></div>
                <h3 className="font-display text-2xl uppercase leading-none">{c.title}</h3>
                <p className="text-zinc-300 mt-1 text-lg">{c.champion}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ROSTER PREVIEW */}
      <section className="px-6 md:px-12 lg:px-20 py-24 bg-[#050505]" data-testid="home-roster">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <SectionHeading kicker="The Talent" title="Superstars" className="mb-0" />
          <Link to="/roster" className="font-display text-xl uppercase text-brand hover:text-brand-hi flex items-center gap-2">
            Full Roster <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {wrestlers.filter((w) => w.featured > 0).sort((a, b) => a.featured - b.featured).slice(0, 4).map((w, i) => (
            <Link key={w.id} to={`/roster/${w.id}`} data-testid={`home-wrestler-${w.id}`} className="relative group border border-zinc-800 overflow-hidden aspect-[3/4]">
              <img src={w.image} alt={w.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-0 p-4">
                <div className="w-6 h-[2px] bg-brand mb-2" />
                <h3 className="font-display text-2xl uppercase leading-none">{w.name}</h3>
                <p className="text-brand text-xs font-mono uppercase tracking-wider mt-1">{w.nickname}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* COMMUNITY PARTNER - EAST TEXAS FOOD BANK */}
      <section className="px-6 md:px-12 lg:px-20 py-24 bg-[#050505]" data-testid="home-community">
        <div className="border-2 border-zinc-800 bg-[#0d0d0d] grid grid-cols-1 lg:grid-cols-2 overflow-hidden">
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3">
              <span className="w-8 h-[2px] bg-brand" />
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-brand">Community Partner</span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">
              Tag-Teaming With The<br /><span className="text-brand">East Texas Food Bank</span>
            </h2>
            <p className="text-zinc-400 mt-5 leading-relaxed max-w-lg">
              CWF has tag-teamed with the East Texas Food Bank to raise awareness of the resources they provide for families facing food insecurity across our region. Their mission says it all — <span className="text-white font-semibold">Fighting Hunger. Feeding Hope.</span>
            </p>
            <p className="text-zinc-300 mt-4 leading-relaxed max-w-lg">
              Want to help? The East Texas Food Bank has simple ways to donate and contribute directly on their website. Every gift helps put a meal on a neighbor's table.
            </p>
            <a href="https://www.easttexasfoodbank.org" target="_blank" rel="noreferrer" data-testid="community-etfb-cta"
              className="mt-8 w-fit bg-brand hover:bg-brand-hi text-black font-display text-xl uppercase tracking-wide px-8 py-4 transition-colors">
              Donate & Learn More
            </a>
          </div>
          <div className="bg-[#2a2d3a] flex items-center justify-center p-6 md:p-8">
            <img src="https://customer-assets.emergentagent.com/job_wrestling-federation/artifacts/9rvmtb71_ETFB%20Logo.jpg" alt="East Texas Food Bank" className="w-full max-w-md max-h-96 object-contain" />
          </div>
        </div>
      </section>
    </div>
  );
}
